import {
  FullStory,
  init as initFullStory,
  isInitialized as isFullStoryInitialized,
} from '@fullstory/browser'
import { AnalyticsBrowser, type EventProperties } from '@segment/analytics-next'
import { getCookie } from 'cookies-next/client'
import { usePathname } from 'next/navigation'
import { useRouter } from 'next/router'
import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type FC,
  type PropsWithChildren,
} from 'react'
import AnalyticsConstants, { SEGMENT_CDN_URL } from '../../constants/analytics'
import appNames from '../../constants/appNames'
import { getJwtFromCookieInClient } from '../lib/api'
import { checkSetAndGetSessionIdInClient } from '../lib/checkSetAndGetSessionId'
import { isGa4PageViewEvent, sendGa4Event } from '../lib/ga4'
import hashEmail from '../lib/hashEmail'
import sendLog from '../lib/sendLog'
import { getErrorMessage, getUtmParams } from '../lib/utils'
import { getApplicationIdFromJwt } from '../utils/jwtUtils'

class TrackingServiceError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TrackingServiceError'
  }
}

const withHashedEmail = async (properties: EventProperties): Promise<void> => {
  // Sent alongside the plain value, which other destinations still need.
  if (typeof properties.email === 'string') {
    const emailSha1 = await hashEmail(properties.email)

    if (emailSha1) {
      properties.emailSha1 = emailSha1
    }
  }
}

const AnalyticsContext = createContext<
  | {
      analytics: AnalyticsBrowser | undefined
      track: (event: string, properties?: EventProperties) => Promise<void>
      identify: (properties?: EventProperties) => Promise<void>
      setSubChannel: (subChannel: string) => void
      events: Record<string, Record<string, string>>
      sessionId: string
    }
  | undefined
  | {
      sessionId: string
      track: null
      identify: null
      events: Record<string, Record<string, string>>
      analytics: null
      setSubChannel: (subChannel: string) => void
    }
>(undefined)

type AnalyticsProviderProps = PropsWithChildren<{ sessionId: string }>

export const AnalyticsProvider: FC<AnalyticsProviderProps> = ({ children, sessionId }) => {
  const pathname = usePathname()
  const { query } = useRouter()
  const persistentSessionId = checkSetAndGetSessionIdInClient(sessionId)
  const [subChannel, setSubChannel] = useState<string | null>(null)

  // Initialize FullStory once at the provider level
  useLayoutEffect(() => {
    if (
      process.env.ENABLE_FULLSTORY === 'true' &&
      process.env.FULLSTORY_ORG_ID &&
      !isFullStoryInitialized()
    ) {
      initFullStory({ orgId: process.env.FULLSTORY_ORG_ID })
    }
  }, [])

  const analytics = useMemo(() => {
    const writeKey = process.env.NEXT_PUBLIC_SEGMENT_WRITEKEY || ''
    if (!writeKey) return undefined

    const segmentInstance = AnalyticsBrowser.load({ writeKey, cdnURL: SEGMENT_CDN_URL })
    segmentInstance.setAnonymousId(persistentSessionId)

    return segmentInstance
  }, [persistentSessionId])

  const getPropertiesToSend = (properties: EventProperties | undefined): EventProperties => {
    const propertiesToSend = properties ? { ...properties } : {}
    const jwt = getJwtFromCookieInClient()
    const fbc = getCookie('_fbc')

    if (jwt && typeof jwt === 'string') {
      const applicationId = getApplicationIdFromJwt(jwt)
      propertiesToSend.applicationId = applicationId
    }

    if (fbc) {
      propertiesToSend.fbc = fbc
    }

    propertiesToSend.utmParams = getUtmParams(query)

    if (subChannel) {
      propertiesToSend.subChannel = subChannel
    }

    return propertiesToSend
  }

  const identify = async (properties?: EventProperties): Promise<void> => {
    try {
      const propertiesToSend = getPropertiesToSend(properties)

      await withHashedEmail(propertiesToSend)

      await analytics?.identify(propertiesToSend)

      if (isFullStoryInitialized()) {
        FullStory('setProperties', {
          type: 'user',
          properties: propertiesToSend,
        })
      }
    } catch (error) {
      sendLog(
        new TrackingServiceError(
          `Analytics.identify could not call analytics.identify: ${getErrorMessage(error)}`
        )
      )
    }
  }

  const track = async (event: string, properties?: EventProperties): Promise<void> => {
    try {
      const propertiesToSend = getPropertiesToSend(properties)
      const eventPrefix = appNames[pathname] ? `${appNames[pathname]} - ` : ''

      await withHashedEmail(propertiesToSend)

      await analytics?.track(`${eventPrefix}${event}`, propertiesToSend)

      if (isFullStoryInitialized()) {
        FullStory('trackEvent', {
          name: `${eventPrefix}${event}`,
          properties: propertiesToSend,
        })
      }

      if (!isGa4PageViewEvent(event)) {
        sendGa4Event(event, propertiesToSend)
      }
    } catch (error) {
      sendLog(
        new TrackingServiceError(
          `Analytics.track could not call analytics.track: ${getErrorMessage(error)}`
        )
      )
    }
  }

  const events = AnalyticsConstants.EVENTS

  if (!analytics)
    // While we wait for Segment to load, we can still return initialized variables
    return (
      <AnalyticsContext.Provider
        value={{
          sessionId: persistentSessionId,
          events,
          track: null,
          identify: null,
          analytics: null,
          setSubChannel,
        }}
      >
        {children}
      </AnalyticsContext.Provider>
    )

  return (
    <AnalyticsContext.Provider
      value={{
        analytics,
        track,
        identify,
        setSubChannel,
        events,
        sessionId: persistentSessionId,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  )
}

// Create an analytics hook that we can use with other components.
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext)
  if (context === undefined) {
    throw new Error('useAnalytics must be used within the AnalyticsProvider')
  }

  return context
}
